import React, { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Edit, Trash2 } from "lucide-react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Icons } from "../../../components/ui/icons";
import SelectInput from "../../../components/select-input";
import SearchInput from "../../../components/search-input";
import DataTable, { TableRowData } from "../../../components/data-table";
import ServiceModals from "./ServiceModals";
import { SERVICES_STATUS_OPTIONS } from "./constants";
import type { MobileAppTab } from "../constants";
import { isServicesTab } from "../constants";
import {
  siteServiceRequestsTableColumns,
  siteServiceTypesTableColumns,
} from "../../../lib/table-columns";
import {
  SiteServiceRecordStatus,
  SiteServiceType,
  createSiteServiceType,
  deleteSiteServiceType,
  getSiteServiceRequests,
  getSiteServiceTypes,
  removeSiteServiceTypeIcon,
  updateSiteServiceType,
  uploadSiteServiceTypeIcon,
} from "../../../lib/api";
import { getBackendErrorMessage } from "../../../lib/utils";

export type ServicesSectionHandle = {
  openCreateType: () => void;
};

interface ServicesSectionProps {
  activeTab: MobileAppTab;
}

const ServicesSection = forwardRef<ServicesSectionHandle, ServicesSectionProps>(
  function ServicesSection({ activeTab }, ref) {
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);
  const [typeName, setTypeName] = useState("");
  const [typeIconUrl, setTypeIconUrl] = useState("");
  const [typeStatus, setTypeStatus] = useState<SiteServiceRecordStatus>("ACTIVE");
  const [typeIconPreview, setTypeIconPreview] = useState<string | null>(null);
  const [savingType, setSavingType] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const iconInputRef = useRef<HTMLInputElement>(null);
  const [deletingTypeId, setDeletingTypeId] = useState<string | null>(null);
  const [deletingType, setDeletingType] = useState(false);

  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);

  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, serviceTypeFilter, activeTab]);

  const statusParam =
    statusFilter === "all" ? undefined : (statusFilter as SiteServiceRecordStatus);

  const serviceTypesQuery = useQuery({
    queryKey: [
      "site-services",
      "types",
      { page: currentPage, limit: pageSize, search: searchTerm, status: statusParam },
    ],
    queryFn: async () =>
      getSiteServiceTypes(currentPage, pageSize, searchTerm, statusParam),
    enabled: activeTab === "Service Types",
    placeholderData: keepPreviousData,
  });

  const allServiceTypesQuery = useQuery({
    queryKey: ["site-services", "types", "all"],
    queryFn: async () => getSiteServiceTypes(1, 200, "", undefined),
    enabled: activeTab === "Requests" || isTypeModalOpen,
  });

  const selectedServiceTypeName = useMemo(() => {
    if (serviceTypeFilter === "all") return undefined;
    const match = (allServiceTypesQuery.data?.data ?? []).find(
      (t) => t.id === serviceTypeFilter,
    );
    return match?.name;
  }, [serviceTypeFilter, allServiceTypesQuery.data]);

  const requestsQuery = useQuery({
    queryKey: [
      "site-services",
      "requests",
      {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        serviceTypeName: selectedServiceTypeName,
      },
    ],
    queryFn: async () =>
      getSiteServiceRequests(currentPage, pageSize, searchTerm, {
        serviceTypeName: selectedServiceTypeName,
      }),
    enabled: activeTab === "Requests",
    placeholderData: keepPreviousData,
  });

  const typeRows = useMemo(() => {
    const data = serviceTypesQuery.data?.data ?? [];
    return data.map((t: SiteServiceType) => ({
      id: t.id,
      icon_url: t.iconUrl ?? "",
      name: t.name,
      status: t.status,
      created_at: format(new Date(t.createdAt), "MMM dd, yyyy"),
      raw_data: t,
    })) as TableRowData[];
  }, [serviceTypesQuery.data]);

  const requestRows = useMemo(() => {
    const data = requestsQuery.data?.data ?? [];
    return data.map((r) => ({
      id: r.id,
      reference: r.reference,
      service_type: r.serviceType?.name ?? "—",
      customer: r.user?.fullName ?? r.user?.email ?? "—",
      attachment_count: String(r.attachments?.length ?? 0),
      created_at: format(new Date(r.createdAt), "MMM dd, yyyy HH:mm"),
      raw_data: r,
    })) as TableRowData[];
  }, [requestsQuery.data]);

  const serviceTypeFilterOptions = useMemo(
    () => [
      { value: "all", label: "All service types" },
      ...(allServiceTypesQuery.data?.data ?? []).map((t) => ({
        value: t.id,
        label: t.name,
      })),
    ],
    [allServiceTypesQuery.data],
  );

  const typesTableLoading =
    activeTab === "Service Types" &&
    serviceTypesQuery.isFetching &&
    !serviceTypesQuery.isLoading;
  const requestsTableLoading =
    activeTab === "Requests" &&
    requestsQuery.isFetching &&
    !requestsQuery.isLoading;

  const totalItems =
    activeTab === "Service Types"
      ? (serviceTypesQuery.data?.meta.total ?? 0)
      : (requestsQuery.data?.meta.total ?? 0);

  const totalPages =
    activeTab === "Service Types"
      ? (serviceTypesQuery.data?.meta.totalPages ?? 1)
      : (requestsQuery.data?.meta.totalPages ?? 1);


  const resetTypeForm = () => {
    setEditingTypeId(null);
    setTypeName("");
    setTypeIconUrl("");
    setTypeStatus("ACTIVE");
    setTypeIconPreview(null);
  };

  const openCreateType = () => {
    resetTypeForm();
    setIsTypeModalOpen(true);
  };

  useImperativeHandle(ref, () => ({
    openCreateType,
  }));

  const openEditType = (raw: SiteServiceType) => {
    setEditingTypeId(raw.id);
    setTypeName(raw.name);
    setTypeIconUrl(raw.iconUrl?.startsWith("http") ? raw.iconUrl : "");
    setTypeStatus(raw.status);
    setTypeIconPreview(raw.iconUrl);
    setIsTypeModalOpen(true);
  };

  const handleSaveType = async () => {
    if (!typeName.trim()) return toast.error("Service type name is required");
    setSavingType(true);
    try {
      const payload = {
        name: typeName.trim(),
        status: typeStatus,
        ...(typeIconUrl.trim() ? { iconUrl: typeIconUrl.trim() } : {}),
      };
      if (editingTypeId) {
        await updateSiteServiceType(editingTypeId, payload);
        toast.success("Service type updated");
      } else {
        await createSiteServiceType(payload);
        toast.success("Service type created");
      }
      setIsTypeModalOpen(false);
      resetTypeForm();
      await queryClient.invalidateQueries({ queryKey: ["site-services", "types"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to save service type"));
    } finally {
      setSavingType(false);
    }
  };

  const handleDeleteType = async () => {
    if (!deletingTypeId) return;
    setDeletingType(true);
    try {
      await deleteSiteServiceType(deletingTypeId);
      toast.success("Service type deleted");
      setDeletingTypeId(null);
      await queryClient.invalidateQueries({ queryKey: ["site-services", "types"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to delete service type"));
    } finally {
      setDeletingType(false);
    }
  };

  const handleIconSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingTypeId) return;
    setUploadingIcon(true);
    try {
      const updated = await uploadSiteServiceTypeIcon(editingTypeId, file);
      setTypeIconPreview(updated.iconUrl);
      setTypeIconUrl("");
      toast.success("Icon uploaded");
      await queryClient.invalidateQueries({ queryKey: ["site-services", "types"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to upload icon"));
    } finally {
      setUploadingIcon(false);
      if (iconInputRef.current) iconInputRef.current.value = "";
    }
  };

  const handleRemoveIcon = async () => {
    if (!editingTypeId) return;
    setUploadingIcon(true);
    try {
      await removeSiteServiceTypeIcon(editingTypeId);
      setTypeIconPreview(null);
      setTypeIconUrl("");
      toast.success("Icon removed");
      await queryClient.invalidateQueries({ queryKey: ["site-services", "types"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to remove icon"));
    } finally {
      setUploadingIcon(false);
    }
  };

  const typeActions = [
    {
      label: "Edit service type",
      icon: Edit,
      onClick: (row: TableRowData) => openEditType((row as any).raw_data),
    },
    {
      label: "Delete service type",
      icon: Trash2,
      onClick: (row: TableRowData) => setDeletingTypeId(row.id as string),
    },
  ];

  const requestActions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => setSelectedRequestId(row.id as string),
    },
  ];

  const closeTypeModal = () => {
    setIsTypeModalOpen(false);
    resetTypeForm();
  };

  return (
    <>
      {isServicesTab(activeTab) && (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-2 w-full">
          <SearchInput
            searchTerm={searchInput}
            onSearchChange={setSearchInput}
            placeholder={
              activeTab === "Requests"
                ? "Search reference, service, customer..."
                : "Search service types..."
            }
          />
          {activeTab === "Service Types" && (
            <SelectInput
              options={SERVICES_STATUS_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              className="ml-auto"
            />
          )}
          {activeTab === "Requests" && (
            <SelectInput
              options={serviceTypeFilterOptions}
              value={serviceTypeFilter}
              onChange={setServiceTypeFilter}
              className="ml-auto"
            />
          )}
        </div>

        {activeTab === "Service Types" && (
          <DataTable
            columns={siteServiceTypesTableColumns}
            data={typeRows}
            actions={typeActions}
            loading={serviceTypesQuery.isLoading || typesTableLoading}
            pagination={{
              page: currentPage,
              pageSize,
              totalItems,
              totalPages,
              onPageChange: setCurrentPage,
              onPageSizeChange: setPageSize,
            }}
          />
        )}

        {activeTab === "Requests" && (
          <DataTable
            columns={siteServiceRequestsTableColumns}
            data={requestRows}
            actions={requestActions}
            loading={requestsQuery.isLoading || requestsTableLoading}
            pagination={{
              page: currentPage,
              pageSize,
              totalItems,
              totalPages,
              onPageChange: setCurrentPage,
              onPageSizeChange: setPageSize,
            }}
          />
        )}
      </div>
      )}

      <ServiceModals
        isTypeModalOpen={isTypeModalOpen}
        onCloseTypeModal={closeTypeModal}
        editingTypeId={editingTypeId}
        typeName={typeName}
        onTypeNameChange={setTypeName}
        typeStatus={typeStatus}
        onTypeStatusChange={setTypeStatus}
        typeIconUrl={typeIconUrl}
        onTypeIconUrlChange={setTypeIconUrl}
        typeIconPreview={typeIconPreview}
        savingType={savingType}
        onSaveType={handleSaveType}
        uploadingIcon={uploadingIcon}
        iconInputRef={iconInputRef}
        onIconSelect={(e) => void handleIconSelect(e)}
        onRemoveIcon={() => void handleRemoveIcon()}
        deletingTypeId={deletingTypeId}
        onCloseDeleteType={() => {
          if (!deletingType) setDeletingTypeId(null);
        }}
        deletingType={deletingType}
        onDeleteType={handleDeleteType}
        selectedRequestId={selectedRequestId}
        onCloseRequestDetails={() => setSelectedRequestId(null)}
      />
    </>
  );
},
);

export default ServicesSection;
